import * as React from "react";
import { useConvex } from "convex/react";
import { ConvexError } from "convex/values";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/convexApi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { clientReportGeneratedAt, clientReportPeriod, clientReportScope, downloadClientReport, type ClientReport } from "./client-report";

interface ClientReportFilters {
  userId?: string;
  dateRange: "30d" | "90d" | "6mo" | "1y" | "all";
  productId?: string;
  deliveredOnly: boolean;
  cargoSearch?: string;
  contractSearch?: string;
}

export function ClientReportButton({ filters, enabled, orderScope }: {
  filters: ClientReportFilters;
  enabled: boolean;
  orderScope: "orders" | "abandoned" | "both";
}) {
  const convex = useConvex();
  const [loading, setLoading] = React.useState(false);
  const [report, setReport] = React.useState<ClientReport | null>(null);
  const disabledReason = !filters.userId
    ? "Select a client to create a Client Report."
    : orderScope !== "orders"
      ? "Choose Completed Orders to report purchases."
      : null;

  async function generateReport() {
    if (!enabled || disabledReason || loading) return;
    setLoading(true);
    try {
      const result = await convex.query(api.analytics.getClientReport, filters) as ClientReport;
      setReport(result);
    } catch (error) {
      toast.error(error instanceof ConvexError && typeof error.data === "string"
        ? error.data
        : "Unable to generate the client report. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={generateReport} disabled={!enabled || Boolean(disabledReason) || loading}>
          {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
          {loading ? "Preparing report…" : "Client Report"}
        </Button>
        <p className="text-xs text-muted-foreground">
          {disabledReason ?? "Client-facing pounds and average price by origin and product."}
        </p>
      </div>
      <Dialog open={report !== null} onOpenChange={(open) => { if (!open) setReport(null); }}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Client Purchase Report</DialogTitle>
            <DialogDescription>
              {report ? `${report.clientName} · ${clientReportPeriod(report)} (UTC)` : "Client purchases"}
            </DialogDescription>
          </DialogHeader>
          {report && <>
            <p className="text-xs text-muted-foreground tabular-nums">Generated at: {clientReportGeneratedAt(report)}</p>
            <p className="text-sm text-muted-foreground">{clientReportScope(report)}. Average prices are weighted by pounds at the recorded item price, before order-level discounts, freight, and tax.</p>
            {report.rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No purchases match these filters.</p>
            ) : (
              <div className="max-h-[50vh] overflow-auto rounded-md border">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Origin</TableHead><TableHead>Product</TableHead>
                    <TableHead className="text-right">Pounds</TableHead><TableHead className="text-right">Avg price / lb (USD)</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{report.rows.map((row, index) => <TableRow key={index}>
                    <TableCell>{row.origin}</TableCell><TableCell>{row.product}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.pounds.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-right tabular-nums">${row.averagePricePerPound.toFixed(2)}</TableCell>
                  </TableRow>)}</TableBody>
                </Table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReport(null)}>Close</Button>
              <Button disabled={report.rows.length === 0} onClick={() => downloadClientReport(report)}>
                <Download className="size-4" aria-hidden="true" /> Download CSV
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}
